import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, Clock, TrendingUp, TrendingDown, RefreshCcw, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { TrailTimeline } from "./TrailTimeline";

// Maps a Deriv contract_type → short human label shown in the row.
const contractLabel = (t) => {
  const u = (t || "").toUpperCase();
  if (u === "CALL") return "Rise";
  if (u === "PUT") return "Fall";
  if (u === "MULTUP") return "Mult ↑";
  if (u === "MULTDOWN") return "Mult ↓";
  if (u === "DIGITEVEN") return "Even";
  if (u === "DIGITODD") return "Odd";
  if (u === "DIGITDIFF") return "Differ";
  return u || "—";
};

const Status = ({ status }) => {
  if (status === "won")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono bg-vts-profit/15 text-vts-profit border border-vts-profit/30">
        <CheckCircle2 className="h-3 w-3" /> Won
      </span>
    );
  if (status === "lost")
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono bg-vts-loss/15 text-vts-loss border border-vts-loss/30">
        <XCircle className="h-3 w-3" /> Lost
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono bg-vts-surface text-vts-silver/70 border border-vts-line">
      <Clock className="h-3 w-3" /> Open
    </span>
  );
};

const DirIcon = ({ contractType }) => {
  const t = (contractType || "").toUpperCase();
  if (t === "CALL" || t === "MULTUP") return <TrendingUp className="h-3.5 w-3.5 text-vts-profit" />;
  if (t === "PUT" || t === "MULTDOWN") return <TrendingDown className="h-3.5 w-3.5 text-vts-loss" />;
  return <span className="h-3.5 w-3.5 inline-block" />;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
};

const RecentTradesInline = ({ onOpenFullPage }) => {
  const { session } = useAuth();
  const accountId = session?.account_id;
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // v8.9.3 — per-row expansion state for trail timeline view.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (cid) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const fetchRecent = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/track/trades", {
        params: { account_id: accountId, limit: 10, skip: 0 },
      });
      setTrades(res.data?.trades || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchRecent();
    const t = setInterval(fetchRecent, 12000);
    return () => clearInterval(t);
  }, [fetchRecent]);

  const wins = trades.filter((t) => t.status === "won").length;
  const losses = trades.filter((t) => t.status === "lost").length;
  const open = trades.filter((t) => t.status === "open").length;
  const pnl = trades.reduce((a, t) => a + (Number(t.profit) || 0), 0);
  const settled = wins + losses;
  const winRate = settled ? Math.round((wins / settled) * 100) : 0;

  return (
    <div className="vts-card p-5" data-testid="history-inline">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-heading font-semibold text-base">Recent trades</h3>
          <p className="text-[11px] text-vts-silver/60 font-mono">
            Last 10 contracts · live · refreshes every 12s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="history-inline-refresh"
            onClick={fetchRecent}
            className="text-vts-silver/70 hover:text-white p-1.5 rounded transition"
            title="Refresh"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            data-testid="history-inline-open-full"
            onClick={onOpenFullPage}
            className="text-[11px] flex items-center gap-1 text-vts-blue hover:text-vts-blueHover font-medium"
          >
            Full history <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Compact summary strip */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-2 py-2 text-center">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">Trades</div>
          <div className="text-sm font-mono text-white tabular-nums">{trades.length}</div>
        </div>
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-2 py-2 text-center">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">Win rate</div>
          <div className="text-sm font-mono text-vts-blue tabular-nums">{winRate}%</div>
        </div>
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-2 py-2 text-center">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">Open</div>
          <div className="text-sm font-mono text-vts-silver tabular-nums">{open}</div>
        </div>
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-2 py-2 text-center">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">P&amp;L</div>
          <div className={`text-sm font-mono tabular-nums ${pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>
            {pnl >= 0 ? "+" : ""}{pnl.toFixed(2)}
          </div>
        </div>
      </div>

      {error && (
        <div className="text-[11px] text-vts-loss bg-vts-loss/10 border border-vts-loss/30 px-2.5 py-2 rounded-md mb-3 font-mono">
          {error}
        </div>
      )}

      {trades.length === 0 && !loading && !error && (
        <div className="text-center py-10 text-vts-silver/50 text-sm">
          No trades yet. Place a trade or start the Bot to see activity here.
        </div>
      )}

      {trades.length > 0 && (
        <div className="divide-y divide-vts-line/60 border border-vts-line rounded-md overflow-hidden">
          {trades.map((t) => {
            const cid = t.contract_id || t.id || t.created_at;
            const isMultiplier = ["MULTUP", "MULTDOWN"].includes((t.contract_type || "").toUpperCase());
            const settled = t.status === "won" || t.status === "lost";
            // Timeline button only on settled multiplier trades — those are
            // the only ones with potential trail events recorded.
            const canExpand = settled && isMultiplier;
            const isOpen = expanded.has(cid);
            return (
            <div key={cid}>
            <div
              data-testid={`history-inline-row-${t.contract_id || t.id}`}
              className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 px-3 py-2.5 hover:bg-vts-surface/30 transition"
            >
              <DirIcon contractType={t.contract_type} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-white truncate">{t.symbol || "—"}</span>
                  <span className="text-[10px] font-mono text-vts-silver/70 uppercase">
                    {contractLabel(t.contract_type)}
                  </span>
                  {t.strategy && (
                    <span className="text-[9px] font-mono text-vts-blue/80 uppercase tracking-wider px-1.5 py-0.5 rounded bg-vts-blue/10 border border-vts-blue/20">
                      {t.strategy}
                    </span>
                  )}
                  {t.secured_at != null && (
                    <span
                      data-testid={`history-inline-secured-${t.contract_id || t.id}`}
                      className="text-[9px] font-mono text-amber-300 uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400/10 border border-amber-400/30"
                      title={`Auto-Secure sold this contract early at +${t.secured_at}% of its max payout`}
                    >
                      🔒 secured @ {t.secured_at}%
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-mono text-vts-silver/50 mt-0.5">
                  {fmtTime(t.created_at)} · stake ${Number(t.stake || 0).toFixed(2)}
                </div>
                {/* "Why did the bot trade this?" — strategy rank, win-rate and
                    sample size at the moment of entry, stamped by the bot. */}
                {t.entry_win_rate != null && (
                  <div
                    data-testid={`history-inline-why-${t.contract_id || t.id}`}
                    className="text-[9px] font-mono text-vts-silver/45 mt-0.5"
                  >
                    why: {t.entry_rank === 0 ? "priority hunt (MA 25/100)" : t.entry_rank ? `#${t.entry_rank} ranked` : "ranked"}
                    {" "}· {Number(t.entry_win_rate).toFixed(0)}% WR · {t.entry_trades || 0} samples at entry
                  </div>
                )}
                {/* v8.9.2 — Live trail status. Shows armed state + peak +
                    floor on every open trade so the protection state is
                    always visible. Pre-arm, shows "tracking · peak +X%"
                    so the user can see progression toward the arm
                    threshold. */}
                {t.status === "open" && t.peak_pct != null && (
                  <div
                    data-testid={`history-inline-lock-${t.contract_id || t.id}`}
                    className="text-[9px] font-mono mt-1 flex items-center gap-2 flex-wrap"
                  >
                    {t.trail_armed ? (
                      <span className="px-1.5 py-0.5 rounded bg-vts-profit/15 text-vts-profit border border-vts-profit/30">
                        🔒 armed @ +{Number(t.peak_pct).toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-vts-silver/55">
                        tracking · peak +{Number(t.peak_pct).toFixed(1)}%
                      </span>
                    )}
                    {t.ratchet_floor != null && Number(t.ratchet_floor) !== 0 && (
                      <span className="text-vts-profit">
                        · floor +{Number(t.ratchet_floor).toFixed(1)}%
                        {t.ratchet_tier && ` · tier ${t.ratchet_tier}`}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right">
                <Status status={t.status} />
              </div>
              <div className="text-right tabular-nums">
                {t.status === "open" || t.profit == null ? (
                  <span className="text-[11px] text-vts-silver/50 font-mono">—</span>
                ) : (
                  <span
                    className={`text-xs font-mono font-semibold ${
                      Number(t.profit) >= 0 ? "text-vts-profit" : "text-vts-loss"
                    }`}
                  >
                    {Number(t.profit) >= 0 ? "+" : ""}
                    {Number(t.profit).toFixed(2)}
                  </span>
                )}
              </div>
              {/* v8.9.3 — Trail timeline expand button. Only enabled on
                  settled multiplier trades. */}
              {canExpand ? (
                <button
                  type="button"
                  data-testid={`history-inline-timeline-toggle-${t.contract_id || t.id}`}
                  onClick={() => toggleExpanded(cid)}
                  className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-vts-line hover:border-vts-silver/40 hover:bg-vts-surface text-vts-silver/65 hover:text-white transition"
                  title="Show trail timeline"
                >
                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  trail
                </button>
              ) : (
                <span className="w-[60px]" />
              )}
            </div>
            {canExpand && isOpen && (
              <div className="bg-vts-bg/50 border-t border-vts-line/60">
                <TrailTimeline contractId={String(t.contract_id || t.id)} />
              </div>
            )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentTradesInline;
